"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import type { User } from "./user-types"
import { atualizarUsuario, buscarUsuarioPorEmail, buscarUsuarios, deletarUsuario, salvarUsuario } from "./users-data"

// Rotas públicas que não precisam carregar usuários
const PUBLIC_ROUTES = ['/login', '/cadastro', '/logout'] as const

/**
 * Verifica se há sessão ativa (sem fazer chamada à API).
 *
 * Pós-migração Better Auth: lê `localStorage.agros_auth` (storage usado pelo
 * `AuthManager` em `lib/(v2)/auth/client`). O cookie `user-session` legado
 * não é mais emitido.
 */
function hasActiveSession(): boolean {
  if (typeof window === 'undefined') return false

  try {
    return window.localStorage.getItem('agros_auth') !== null
  } catch {
    return false
  }
}

interface UsersContextType {
  users: User[]
  loading: boolean
  addUser: (user: Omit<User, "id" | "createdAt">) => Promise<void>
  updateUser: (id: string, user: Partial<User>) => Promise<void>
  deleteUser: (id: string) => Promise<void>
  getUserByEmail: (email: string) => Promise<User | null>
  refreshUsers: () => Promise<void>
}

const UsersContext = createContext<UsersContextType | undefined>(undefined)

// Usuário padrão do sistema
const defaultUsers: User[] = [
  {
    id: "1",
    name: "Comercial Innovagro",
    email: "comercial@innovagrobrasil.com.br",
    role: "Super Usuario",
    active: true,
    createdAt: new Date().toISOString(),
  },
]

export function UsersProvider({ children }: { children: ReactNode }) {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)

  // Carregar usuários do banco na inicialização (apenas se autenticado)
  const refreshUsers = async () => {
    // 🚫 Skip em rotas públicas
    if (typeof window !== 'undefined') {
      const isPublicRoute = PUBLIC_ROUTES.some(route =>
        window.location.pathname.startsWith(route)
      )
      if (isPublicRoute) {
        setUsers([])
        setLoading(false)
        return
      }
    }
    setLoading(true)

    // Verificar sessão ativa (sem chamar API)
    if (!hasActiveSession()) {
      setUsers([])
      setLoading(false)
      return
    }
    
    try {
      const usuariosDoBanco = await buscarUsuarios()
      if (usuariosDoBanco && usuariosDoBanco.length > 0) {
        setUsers(usuariosDoBanco)
      } else {
        setUsers(defaultUsers)
      }
      
      // FORÇAR que sempre tenha pelo menos um usuário
      setTimeout(() => {
        setUsers(prev => {
          if (prev.length === 0) {
            return defaultUsers
          }
          return prev
        })
      }, 1000)
    } catch (error) {
      console.error("❌ [USERS-CONTEXT] Erro ao carregar usuários:", error)
      console.error("❌ [USERS-CONTEXT] Stack trace:", error instanceof Error ? error.stack : String(error))
      setUsers(defaultUsers)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refreshUsers()
  }, [])

  const addUser = async (userData: Omit<User, "id" | "createdAt">) => {
    setLoading(true)
    try {
      const novoUsuario = await salvarUsuario(userData)
      setUsers((prev) => [...prev, novoUsuario])
    } catch (error) {
      console.error("❌ [USERS] Erro ao adicionar usuário:", error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateUser = async (id: string, userData: Partial<User>) => {
    setLoading(true)
    try {
      const usuarioAtualizado = await atualizarUsuario(id, userData)
      setUsers((prev) => prev.map((user) => (user.id === id ? usuarioAtualizado : user)))
    } catch (error) {
      console.error("❌ [USERS] Erro ao atualizar usuário:", error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deleteUser = async (id: string) => {
    setLoading(true)
    try {
      await deletarUsuario(id)
      setUsers((prev) => prev.filter((user) => user.id !== id))
    } catch (error) {
      console.error("❌ [USERS] Erro ao deletar usuário:", error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getUserByEmail = async (email: string): Promise<User | null> => {
    try {
      // Primeiro tentar no banco
      const usuarioDoBanco = await buscarUsuarioPorEmail(email)
      if (usuarioDoBanco) {
        return usuarioDoBanco
      }

      // Fallback para usuários em memória
      const usuarioLocal = users.find((u) => u.email === email && u.active)
      return usuarioLocal || null
    } catch (error) {
      console.error("❌ [USERS] Erro ao buscar usuário por email:", error)

      // Fallback final para usuários em memória
      const usuarioLocal = users.find((u) => u.email === email && u.active)
      return usuarioLocal || null
    }
  }

  return (
    <UsersContext.Provider
      value={{
        users,
        loading,
        addUser,
        updateUser,
        deleteUser,
        getUserByEmail,
        refreshUsers,
      }}
    >
      {children}
    </UsersContext.Provider>
  )
}

export function useUsers() {
  const context = useContext(UsersContext)
  if (context === undefined) {
    throw new Error("useUsers deve ser usado dentro de um UsersProvider")
  }
  return context
}
